(() => {
  if (customElements.get('benefits-carousel')) return;

  class BenefitsCarousel extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.list = this.querySelector('.benefits-bar__list');
      if (!this.list) return;
      this.items = [...this.list.children];
      this.controls = this.querySelector('[data-benefits-controls]');
      this.toggle = this.querySelector('[data-benefits-toggle]');
      this.smallScreen = matchMedia('(max-width: 1023px)');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.motion.matches;
      this.hovered = false;
      this.focused = false;
      this.dragging = false;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const listen = (target, type, handler) => target?.addEventListener(type, handler, options);

      listen(this.querySelector('[data-benefits-previous]'), 'click', () => this.move(-1));
      listen(this.querySelector('[data-benefits-next]'), 'click', () => this.move(1));
      listen(this.toggle, 'click', () => {
        this.paused = !this.paused;
        this.schedule();
      });
      listen(this, 'mouseenter', () => { this.hovered = true; this.schedule(); });
      listen(this, 'mouseleave', () => { this.hovered = false; this.schedule(); });
      listen(this, 'focusin', () => { this.focused = true; this.schedule(); });
      listen(this, 'focusout', (event) => {
        this.focused = this.contains(event.relatedTarget);
        this.schedule();
      });
      listen(this.list, 'pointerdown', () => { this.dragging = true; this.schedule(); });
      listen(window, 'pointerup', () => { this.dragging = false; this.schedule(); });
      listen(window, 'pointercancel', () => { this.dragging = false; this.schedule(); });
      listen(this.list, 'keydown', (event) => {
        if (event.target !== this.list || !this.active) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          const rtl = getComputedStyle(this.list).direction === 'rtl';
          this.move((event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1));
        }
      });
      listen(document, 'visibilitychange', () => this.schedule());
      listen(this.smallScreen, 'change', () => this.update());
      listen(this.motion, 'change', () => {
        this.paused = this.motion.matches;
        this.schedule();
      });
      listen(document, 'shopify:block:select', (event) => {
        const index = this.items.findIndex((item) => item === event.target || item.contains(event.target));
        if (index < 0) return;
        this.paused = true;
        this.schedule();
        if (this.active) this.goTo(index, 'auto');
      });
      this.update();
    }

    get active() {
      return this.dataset.slider === 'true' && this.smallScreen.matches && this.items.length > 1;
    }

    update() {
      if (this.controls) this.controls.hidden = !this.active;
      this.list.tabIndex = this.active ? 0 : -1;
      if (!this.active) this.list.scrollTo({ left: 0, behavior: 'instant' });
      this.schedule();
    }

    currentIndex() {
      const bounds = this.list.getBoundingClientRect();
      const rtl = getComputedStyle(this.list).direction === 'rtl';
      const edge = rtl ? 'right' : 'left';
      let index = 0;
      let distance = Infinity;
      this.items.forEach((item, candidate) => {
        const offset = Math.abs(item.getBoundingClientRect()[edge] - bounds[edge]);
        if (offset < distance) { distance = offset; index = candidate; }
      });
      return index;
    }

    goTo(index, behavior) {
      const bounds = this.list.getBoundingClientRect();
      const rtl = getComputedStyle(this.list).direction === 'rtl';
      const edge = rtl ? 'right' : 'left';
      const offset = this.items[index].getBoundingClientRect()[edge] - bounds[edge];
      this.list.scrollBy({
        left: offset,
        behavior: behavior || (this.motion.matches ? 'instant' : 'smooth')
      });
    }

    move(direction) {
      if (!this.active) return;
      const next = (this.currentIndex() + direction + this.items.length) % this.items.length;
      this.goTo(next);
      this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      if (this.toggle) {
        this.toggle.textContent = this.paused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel;
      }
      if (!this.active || this.dataset.autoplay !== 'true' || this.paused ||
          this.hovered || this.focused || this.dragging || document.hidden) return;
      const seconds = Math.max(3, Math.min(10, Number(this.dataset.interval) || 5));
      this.timer = setTimeout(() => this.move(1), seconds * 1000);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.controller = null;
    }
  }

  customElements.define('benefits-carousel', BenefitsCarousel);
})();
